import {
    getPagedSearchSurfaceController,
    initPagedSearchSurface,
} from '../../components/paged-search-surface.js';

export function initManualGamesBrowser(
    documentRef = globalThis.document,
    fetchImpl = globalThis.fetch,
    windowRef = globalThis.window,
    timerApi = globalThis,
) {
    const browserRoot = documentRef?.querySelector?.('[data-lists-manual-browser]');
    if (!(browserRoot instanceof HTMLElement)) {
        return undefined;
    }

    const existingController = getPagedSearchSurfaceController(browserRoot);
    if (existingController !== undefined) {
        return existingController;
    }

    return initPagedSearchSurface(browserRoot, {
        windowRef,
        fetchImpl,
        timerApi,
        formSelector: '[data-lists-filter-form]',
        inputSelector: '[data-lists-filter-input]',
        clearSelector: '[data-lists-filter-clear]',
        resultsSelector: '[data-paged-search-results]',
        pageUrlAttribute: 'pagedSearchPageUrl',
        searchParam: 'filter',
        pageParam: 'page',
        debounceMs: 250,
        resolveStateFromResponse: ({ requestedState, results }) => ({
            query: requestedState?.query ?? '',
            page: resolveCurrentPageFromResults(results, requestedState?.page ?? 1),
        }),
        shouldAnimateSwap: (context) => context.source === 'pagination',
        afterSwap: ({ source, results }) => {
            if (source === 'pagination') {
                results.querySelectorAll('.ui-game-card-reveal').forEach((card) => {
                    card.classList.remove('ui-game-card-reveal');
                });
            }
            syncManualListChrome(documentRef);
        },
        afterSettled: (context) => {
            if (context.commitFailed) {
                requireManualListDeleteConfirmation(documentRef);
            }
        },
    });
}

function syncManualListChrome(documentRef = globalThis.document) {
    const manualOrderRoot = documentRef?.querySelector?.('[data-lists-manual-order]');
    if (!(manualOrderRoot instanceof HTMLElement)) {
        return;
    }

    const filterForm = documentRef?.querySelector?.('[data-lists-filter-form]');
    const rawTotalCount = String(manualOrderRoot.dataset.listsManualTotalCount ?? '').trim();
    const totalCount = /^(0|[1-9]\d*)$/.test(rawTotalCount) ? Number(rawTotalCount) : Number.NaN;
    const hasReliableTotalCount = Number.isSafeInteger(totalCount);
    if (filterForm instanceof HTMLFormElement && hasReliableTotalCount) {
        filterForm.hidden = totalCount === 0;
    }

    const countElement = documentRef?.querySelector?.('[data-lists-details-count]');
    if (countElement instanceof HTMLElement && hasReliableTotalCount) {
        countElement.textContent = formatGameCount(totalCount);
    }

    const deleteForm = documentRef?.querySelector?.('[data-lists-manual-delete-form]');
    if (deleteForm instanceof HTMLFormElement) {
        deleteForm.dataset.listsManualDeleteRequiresConfirmation = !hasReliableTotalCount || totalCount > 0
            ? 'true'
            : 'false';
    }
}

function requireManualListDeleteConfirmation(documentRef) {
    const deleteForm = documentRef?.querySelector?.('[data-lists-manual-delete-form]');
    if (deleteForm instanceof HTMLFormElement) {
        deleteForm.dataset.listsManualDeleteRequiresConfirmation = 'true';
    }
}

function resolveCurrentPageFromResults(results, fallbackPage) {
    if (!(results instanceof HTMLElement)) {
        return fallbackPage;
    }

    const pageMarker = results.querySelector('[data-paged-search-current-page]');
    if (!(pageMarker instanceof HTMLElement)) {
        return fallbackPage;
    }

    const parsed = Number.parseInt(String(pageMarker.dataset.pagedSearchCurrentPage ?? ''), 10);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallbackPage;
}

export function formatGameCount(count) {
    const normalizedCount = Number.isFinite(count) ? Math.max(0, count) : 0;

    return normalizedCount === 1 ? '1 game' : `${normalizedCount} games`;
}
